import { configureGpsAccess } from '../utils/gpsAccess';
import React, { useLayoutEffect, createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { discardGlobalAdminWakeQueueItems, flushApiWakeQueue, purgeForeignAndStaleWakeQueueItems } from '../utils/apiWakeQueue';
import { ActingCompany, getActingCompany, onActingCompanyChange, setActingCompany as storeActingCompany, setGlobalAdminSession } from '../utils/actingCompany';
import { clearUserDataCaches, purgeLegacyMyDayCache } from '../utils/offlineDataService';
import { resetSessionScope, scopeFor, setSessionScope } from '../utils/sessionScope';

export interface EnabledModules {
  dashboard?: boolean;
  visits?: boolean;
  routes?: boolean;
  locations?: boolean;
  map?: boolean;
  reports?: boolean;
  users?: boolean;
  vehicles?: boolean;
  fuel?: boolean;
  travel_orders?: boolean;
  vacations?: boolean;
  calendar?: boolean;
  notifications?: boolean;
  gps_tracking?: boolean;
  warehouse?: boolean;
  consumables?: boolean;
  crm?: boolean;
  settings?: boolean;
  [key: string]: boolean | undefined;
}

export interface UploadSettings {
  allow_uploads?: boolean;
  visit_photos?: boolean;
  fuel_receipts?: boolean;
  travel_receipts?: boolean;
  vehicle_damages?: boolean;
  signatures?: boolean;
  [key: string]: boolean | undefined;
}

export interface NotificationSetting {
  email: boolean;
  push: boolean;
}

export interface NotificationPreferences {
  visit_assigned?: NotificationSetting;
  visit_completed?: NotificationSetting;
  route_assigned?: NotificationSetting;
  route_modified?: NotificationSetting;
  fuel_entry_created?: NotificationSetting;
  travel_order_created?: NotificationSetting;
  travel_order_completed?: NotificationSetting;
  travel_order_approved?: NotificationSetting;
  travel_order_rejected?: NotificationSetting;
  vacation_request_created?: NotificationSetting;
  vacation_request_approved?: NotificationSetting;
  vacation_request_rejected?: NotificationSetting;
  vehicle_service_reminder?: NotificationSetting;
  vehicle_registration_reminder?: NotificationSetting;
  vehicle_damage_reported?: NotificationSetting;
  vehicle_assigned?: NotificationSetting;
  inventory_transfer?: NotificationSetting;
  contract_expiry?: NotificationSetting;
  account_locked?: NotificationSetting;
  license_expiry?: NotificationSetting;
  admin_message?: NotificationSetting;
  quiet_hours?: { enabled: boolean; start: string; end: string };
  email_digest?: { enabled: boolean; frequency: string; time: string };
  [key: string]: NotificationSetting | { enabled: boolean; start?: string; end?: string; frequency?: string; time?: string } | undefined;
}

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  role: 'global_admin' | 'company_admin' | 'fleet_manager' | 'hr_admin' | 'technician' | 'warehouse_staff';
  company_id?: string;
  company?: {
    id: string;
    name: string;
    settings?: {
      timezone?: string;
      time_tracking_mode?: 'automatic' | 'manual';
    };
    enabled_modules?: EnabledModules;
    upload_settings?: UploadSettings;
  };
  allowed_modules?: EnabledModules;
  notification_preferences?: NotificationPreferences;
  notification_delegates?: Record<string, string[]>;
  language: 'hr' | 'en';
  is_active: boolean;
  /** Posao 34: firma čeka brisanje (admin firme vidi samo brisanje i izvoz). */
  offboarding?: { id: string; status: string; scheduled_for?: string | null; requested_at?: string; initiator_kind?: string } | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  updateUser: (data: Partial<User>) => void;
  replaceToken: (newToken: string) => void;
  can: (permission: string) => boolean;
  canManageRoles: boolean;
  refreshAccess: () => Promise<void>;
  /** Posao 23-7: prijavljeni korisnik (globalni admin bez firme rada), neovisno o firmi rada. */
  platformUser: User | null;
  /** Firma rada globalnog administratora u ovoj kartici (null = bez firme). */
  actingCompany: ActingCompany | null;
  /** Globalni administrator bez odabrane firme rada (platformski ekrani). */
  isPlatformAdmin: boolean;
  /** Odabir ili izlazak iz firme rada (samo globalni administrator). */
  selectActingCompany: (company: ActingCompany | null) => void;
}

// Posao 23-5c/23-7 (P18): globalni administrator nikad ne odobrava; gumbi se skrivaju.
export const APPROVAL_PERMISSIONS = ['hr.approve_vacation', 'travel.approve_order', 'travel.close_order', 'warehouse.approve_transfer'];

interface RegisterData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [realUser, setUser] = useState<User | null>(null);
  const isGlobal = realUser?.role === 'global_admin';
  const [actingCompany, setActingState] = useState<ActingCompany | null>(() => getActingCompany());
  const [actingDetail, setActingDetail] = useState<User['company'] | null>(null);
  // Zaglavlje firme rada smije se slati čim je poznato da je prijavljen globalni admin.
  setGlobalAdminSession(Boolean(isGlobal));
  useEffect(() => onActingCompanyChange(company => setActingState(company)), []);
  useEffect(() => { if (isGlobal) setActingState(getActingCompany()); else setActingState(null); }, [isGlobal, realUser?.id]);
  // Odluka 13: stare stavke offline reda globalnog admina uklanjaju se bez slanja.
  useEffect(() => { if (isGlobal && realUser?.id) { try { discardGlobalAdminWakeQueueItems(realUser.id); } catch { /* localStorage nedostupan */ } } }, [isGlobal, realUser?.id]);
  // Moduli, postavke i prilozi firme rada (platformska ruta /companies/:id).
  useEffect(() => {
    if (!isGlobal || !actingCompany) { setActingDetail(null); return; }
    let active = true;
    api.get(`/companies/${actingCompany.id}`, { zomaActingCompany: null }).then(({ data }) => { if (active) setActingDetail(data.company); }).catch(() => {});
    return () => { active = false; };
  }, [isGlobal, actingCompany?.id]);
  // Globalni admin u firmi rada: ekrani vide firmu rada kao vlastitu (company_id, moduli).
  const user: User | null = React.useMemo(() => (isGlobal && actingCompany && realUser
    ? { ...realUser, company_id: actingCompany.id, company: { ...(actingDetail || {}), id: actingCompany.id, name: actingDetail?.name || actingCompany.name } }
    : realUser), [realUser, isGlobal, actingCompany, actingDetail]);
  const selectActingCompany = (company: ActingCompany | null) => { if (isGlobal) storeActingCompany(company); };
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState<{ permissions: string[]; can_manage_roles: boolean }>({ permissions: [], can_manage_roles: false });
  const [accessOwner, setAccessOwner] = useState<string | null>(null);
  const refreshAccess = async () => {
    const { data } = await api.get('/role-management/me');
    setAccess(data); setAccessOwner(user?.id || null);
  };
  useEffect(() => {
    if (!user) { setAccess({ permissions: [], can_manage_roles: false }); return; }
    let active = true;
    const refresh = () => api.get('/role-management/me').then(({ data }) => { if (active) { setAccess(data); setAccessOwner(user.id); } }).catch(() => {});
    void refresh();
    window.addEventListener('focus', refresh);
    return () => { active = false; window.removeEventListener('focus', refresh); };
  }, [user?.id, actingCompany?.id]);
  // Posao 23-7: globalni admin bez firme sve (platforma); u firmi rada prava po modulima te
  // firme iz /role-management/me; odobravanja nikad (P18).
  const can = (permission: string) => {
    if (user?.role === 'global_admin') {
      if (APPROVAL_PERMISSIONS.includes(permission)) return false;
      if (!actingCompany) return true;
    }
    return accessOwner === user?.id && access.permissions.includes(permission);
  };
  const gpsGranted = can('gps.track');
  // SEC-01/02: scope sesije prati korisnika (npr. promjena uloge/modula nakon updateUser).
  useLayoutEffect(() => { setSessionScope(user); }, [user]);
  useLayoutEffect(() => { configureGpsAccess(user, gpsGranted); return () => configureGpsAccess(null, false); }, [user?.id, user?.company_id, user?.company?.enabled_modules?.gps_tracking, gpsGranted]);
  const navigate = useNavigate();
  const isInitialized = useRef(false);

  useEffect(() => {
    const initAuth = async () => {
      if (isInitialized.current) return;
      isInitialized.current = true;

      void purgeLegacyMyDayCache().catch(() => {});
      const storedToken = localStorage.getItem('token');

      if (storedToken) {
        try {
          api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          const response = await api.get('/auth/me');
          // Ako se u međuvremenu netko prijavio ili odjavio, ovaj odgovor više ne vrijedi.
          if (localStorage.getItem('token') !== storedToken) {
            setLoading(false);
            return;
          }
          setGlobalAdminSession(response.data.user?.role === 'global_admin');
          resetSessionScope(response.data.user);
          // Pokretanje: stavke reda drugih korisnika i stara karantena brišu se s uređaja.
          try { purgeForeignAndStaleWakeQueueItems(scopeFor(response.data.user)); } catch { /* localStorage nedostupan */ }
          setUser(response.data.user);
          setToken(storedToken);
          void flushApiWakeQueue(api);
        } catch (error) {
          console.error('Prijava nije obnovljena.');
          localStorage.removeItem('token');
          delete api.defaults.headers.common['Authorization'];
          setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    const { token: newToken, user: newUser } = response.data;
    
    if (!newToken || !newUser) {
      throw new Error('Invalid response from server');
    }
    
    localStorage.setItem('token', newToken);
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    // Nova prijava u kartici počinje bez firme rada (odluka 10).
    storeActingCompany(null);
    setGlobalAdminSession(newUser?.role === 'global_admin');
    resetSessionScope(newUser);
    setToken(newToken);
    setUser(newUser);
    setLoading(false);
    void flushApiWakeQueue(api);

    if (newUser.language) {
      localStorage.setItem('language', newUser.language);
    }
    
  };

  const register = async (data: RegisterData) => {
    await api.post('/auth/register', data);
  };

  const logout = () => {
    resetSessionScope(null);
    storeActingCompany(null);
    setGlobalAdminSession(false);
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    void clearUserDataCaches().catch(() => {});
    setToken(null);
    setUser(null);
    navigate('/login');
  };

  const updateUser = (data: Partial<User>) => {
    if (realUser) {
      setUser({ ...realUser, ...data });
    }
  };

  const replaceToken = (newToken: string) => {
    localStorage.setItem('token', newToken);
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateUser, replaceToken, can, canManageRoles: access.can_manage_roles, refreshAccess,
      platformUser: realUser, actingCompany: isGlobal ? actingCompany : null, isPlatformAdmin: Boolean(isGlobal && !actingCompany), selectActingCompany }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
